import Vue from "vue";
import store from "store2";
import {Route} from "vue-router/types/router";
import {ComputedOptions} from "vue/types/options";
import {isFunction} from "@/utils/TypeUtil";

/** Route プロバイダ型 */
type RouteProvider = () => Route;

/** 値もしくは値プロバイダ型 */
type ValueOrProvider<T> = (() => T) | T;

/** 値の変更を監視させるオブジェクトの型 */
type PseudoObserver = {[key: string]: boolean};

/**
 * オプション
 */
export interface PageStoreOptions {
  /** ストレージに値が無かった場合の値、もしくは値のプロバイダ */
  alt: ValueOrProvider<any>;

  /** 保存するキー名 */
  key: string;
}

/**
 * PageStoreMixin インターフェイス
 */
interface PageStoreMixin extends Vue {
  /**
   * pageStore アクセサオブジェクト
   */
  pageStore: {
    /**
     * PageStore から値を取得します
     *
     * @param name 変数名
     * @param options オプション
     */
    get: <T>(name: string, options?: Partial<PageStoreOptions>) => T;

    /**
     * PageStore に値を格納します
     *
     * @param name 変数名
     * @param value PageStore に格納する値
     * @param options オプション
     */
    set: <T>(name: string, value: T, options?: Partial<PageStoreOptions>) => void;
  };
}

/**
 * Vue Component の名称を取得します
 *
 * @param vm Vue
 */
function vmName(vm: Vue) {
  return vm.$options.name;
}

/**
 * 何もしません
 *
 * @param value 値
 */
function through<T>(value: T) {
  return value;
}

/**
 * LocalStorage に格納する際のキーを生成します
 *
 * @param routeProvider RouteProvider
 * @param name 変数名
 * @param options オプション
 */
function buildStoreKey(routeProvider: RouteProvider, name: string, options: Partial<PageStoreOptions>) {
  if(options.key) {
    return options.key;
  }

  const keyPrefix = routeProvider().name
      ?.replace(/^\/|\/$/, '')
      ?.replace(/\//g, '.');

  return  ['pageStore', keyPrefix ?? '', name]
      .filter(k => !!k)
      .join('.');
}

/**
 * PageStore から値を取得します
 *
 * @param routeProvider RouteProvider
 * @param name 変数名
 * @param options オプション
 */
function get<T>(routeProvider: RouteProvider, name: string, options?: Partial<PageStoreOptions>): T | undefined {
  const optionsNn = options ?? {};
  const storeKey = buildStoreKey(routeProvider, name, optionsNn);
  if(store.local.has(storeKey)) {
    return store.local.get(storeKey);
  } else if(isFunction(optionsNn.alt)) {
    return optionsNn.alt();
  } else {
    return optionsNn.alt ?? null;
  }
}

/**
 * PageStore に値を格納します
 *
 * @param routeProvider RouteProvider
 * @param name 変数名
 * @param value PageStore に格納する値
 * @param options オプション
 */
function set<T>(routeProvider: RouteProvider, name: string, value: T, options?: Partial<PageStoreOptions>) {
  const optionsNn = options ?? {};
  const storeKey = buildStoreKey(routeProvider, name, optionsNn);
  store.local.set(storeKey, value, true);
}


/** 値の変更を監視させるオブジェクトのストア */
const observers: PseudoObserver[] = [];

/** key 指定された際に通知し合うためのマップ。Map のキーが {PageStoreOptions.key} */
const globalKeys: Map<string, {observerId: number; name: string}[]> = new Map();

/** mapPageStores の引数型 */
type PageStoreMapper<N extends string> = { [K in N]: Partial<PageStoreOptions> } | N[];


/**
 * 自動でキーに関連する算出プロパティーを生成します
 * 以下の様に使用して下さい
 *
 * computed: {
 *   ...mapPageStores({
 *     name: {alt: 'default value'}
 *   })
 * }
 *
 * @param mappings マッピング
 */
export function mapPageStores<N extends string>(mappings: PageStoreMapper<N>) {
  if(Array.isArray(mappings)) {
    mappings = mappings.reduce(
        (p, c) => { p[c] = {}; return p; },
        {} as {[K in N]: Partial<PageStoreOptions>}
    );
  }

  const result: {[K: string]: ComputedOptions<any>} = {};
  const observer: PseudoObserver = {};
  const observerId = observers.length;
  for(const [name, options] of Object.entries<Partial<PageStoreOptions>>(mappings)) {
    if(options.key) {
      let gk = globalKeys.get(options.key);
      if(!gk) {
        globalKeys.set(options.key, gk = []);
      }
      gk.push({observerId, name});
    }
    observer[name] = true;
    result[name] = {
      get: function(this: PageStoreMixin) {
        if(!('pageStore' in this)) {
          throw new Error(`Vue Component "${vmName(this)}" does not have PageStoreMixin`);
        }

        // Register dependencies
        through(observers[observerId][name]);

        // Get from PageStore
        return this.pageStore.get(name, options);
      },
      set: function(this: PageStoreMixin, value: any) {
        if(!('pageStore' in this)) {
          throw new Error(`Vue Component "${vmName(this)}" does not have PageStoreMixin`);
        }

        // Set to PageStore
        this.pageStore.set(name, value, options);

        // Notify dependencies
        observers[observerId][name] = !through(observers[observerId][name]);

        // Notify dependencies of the same key
        if(options.key) {
          globalKeys.get(options.key)
              ?.filter(({observerId: oId}) => oId !== observerId)
              ?.forEach(({observerId: oId, name}) => {
                observers[oId][name] = !through(observers[oId][name]);
              });
        }
      },
      cache: true
    };
  }
  observers[observerId] = Vue.observable(observer);
  return result as {[K in N]: ComputedOptions<any>};
}

/**
 * PageStoreMixin 実装
 */
export default Vue.extend({
  computed: {
    pageStore() {
      const routeProvider = () => this.$route;
      return {
        set: set.bind(this, routeProvider),
        get: get.bind(this, routeProvider)
      };
    }
  }
});
